import { registerSW } from 'virtual:pwa-register'

let updatePending = false

/**
 * Registers the service worker built by vite-plugin-pwa (see vite.config.ts).
 * `injectRegister: null` there means this is the only place registration happens,
 * so it must run in every environment that serves a built app (never in dev,
 * since the plugin's `devOptions.enabled` is false and emits no sw.js there).
 *
 * `registerType: 'autoUpdate'` installs and activates a new build on its own. The plugin's
 * default then reloads the page the moment it activates, which after a deploy is a few
 * seconds into the visit and wipes whatever the person was doing. Instead the reload waits
 * for their next move to another screen (`reloadIfUpdatePending`), when the page changes
 * anyway. An open tab checks for a new build when it comes back to the front and every half hour.
 */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return

  registerSW({
    immediate: true,
    onNeedReload() {
      updatePending = true
    },
    // A tab or home-screen app left open never asks for a new build on its own, so it can run
    // yesterday's code for hours. Ask when the app comes back to the front, and every half hour.
    onRegisteredSW(_url, registration) {
      if (!registration) return
      const check = () => {
        if (navigator.onLine) void registration.update().catch(() => {})
      }
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check()
      })
      window.setInterval(check, 30 * 60 * 1000)
    },
  })
}

/** Call on every in-app navigation: loads the new build at the screen just opened. */
export function reloadIfUpdatePending(): void {
  if (updatePending) window.location.reload()
}
